// Persistent "cancels we owe the backend" queue.
//
// Cancel flow: the client aborts the upload locally (removes IDB record,
// releases ownership lock) and POSTs /cancel to the backend. If the POST
// fails or is aborted by a page unload before it lands, the backend row
// stays in a live status (PAUSED / CHUNK_UPLOAD_IN_PROGRESS). On the next
// /sessions/active poll the row is returned, the synth path in the packages
// slice rebuilds a paused card for it, and the user sees the phantom
// "cancelled upload came back" symptom.
//
// This queue makes cancel intent durable across reloads:
//   1. cancel() enqueues BEFORE the POST so a mid-flight reload preserves
//      the signal.
//   2. On boot (and on visibility-wake) a drainer re-POSTs every entry
//      until the backend acknowledges (2xx or 404 → row already gone).
//   3. The synth path (see synthesizeLocalCardsFromBackend) reads the
//      queue and skips any uploadId it contains, so the user never sees
//      the phantom card even during the retry window.
//
// Bounded by TTL (24h) so a permanently unreachable row or a manual
// server-side deletion doesn't wedge the queue forever.

const STORAGE_KEY = "clipn:pending-cancels";
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

const safeParse = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readRaw = () => {
  try {
    if (typeof localStorage === "undefined") return [];
    return safeParse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
};

const writeRaw = (list) => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* private mode / quota — non-fatal */
  }
};

export const enqueuePendingCancel = (uploadId, reason, notes) => {
  if (!uploadId) return;
  const list = readRaw();
  if (list.some((e) => e && e.uploadId === uploadId)) return;
  list.push({ uploadId, reason: reason || null, notes: notes || null, queuedAt: Date.now() });
  writeRaw(list);
};

export const removePendingCancel = (uploadId) => {
  if (!uploadId) return;
  writeRaw(readRaw().filter((e) => e && e.uploadId !== uploadId));
};

export const listPendingCancels = () => readRaw().filter((e) => e && e.uploadId);

export const pendingCancelIdSet = () =>
  new Set(readRaw().map((e) => e?.uploadId).filter(Boolean));

export const hasPendingCancel = (uploadId) =>
  !!uploadId && readRaw().some((e) => e && e.uploadId === uploadId);

// TTL-based cleanup so the queue can't grow forever if the backend is
// permanently unreachable (row garbage-collected, user permanently offline).
export const prunePendingCancels = (ttlMs = DEFAULT_TTL_MS) => {
  const cutoff = Date.now() - ttlMs;
  writeRaw(readRaw().filter((e) => e && typeof e.queuedAt === "number" && e.queuedAt >= cutoff));
};

// Test hook — not exported from any barrel.
export const __clearPendingCancelsForTests = () => writeRaw([]);
